/**
 * Cart item Edit modal / bottom sheet.
 * Opens from [data-cart-edit], loads sections/cart-edit-item, updates via /cart/update.js
 */
(() => {
  const SECTION_ID = 'cart-edit-item';
  const THUMB_HEIGHT = 90;
  const THUMB_GAP = 8;
  const sectionCache = new Map();
  const sectionInflight = new Map();

  const cacheKeyFor = (productUrl, variantId) => `${productUrl}|${variantId || ''}`;

  const fetchEditSection = (productUrl, variantId) => {
    const key = cacheKeyFor(productUrl, variantId);
    if (sectionCache.has(key)) {
      return Promise.resolve(sectionCache.get(key));
    }
    if (sectionInflight.has(key)) {
      return sectionInflight.get(key);
    }

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', SECTION_ID);
    if (variantId) url.searchParams.set('variant', variantId);

    const request = fetch(url.toString())
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        sectionCache.set(key, html);
        sectionInflight.delete(key);
        return html;
      })
      .catch((error) => {
        sectionInflight.delete(key);
        throw error;
      });

    sectionInflight.set(key, request);
    return request;
  };

  class CartEditModal extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.overlay = this.querySelector('[data-cart-edit-overlay]');
      this.panel = this.querySelector('[data-cart-edit-panel]');
      this.content = this.querySelector('[data-cart-edit-content]');
      this.lineContext = null;
      this.abortController = null;
      this.productData = null;
      this.selectedVariant = null;
      this.preferredOptions = null;
      this.currentMedia = [];
      this.activeMediaIndex = 0;
      this.thumbOffset = 0;
      this.visibleThumbCount = 0;
      this.galleryRoot = null;

      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onKeyUp = this.handleKeyUp.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.onPointerOver = this.handlePointerOver.bind(this);
      this.prefetchVisibleEdits = this.prefetchVisibleEdits.bind(this);

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('pointerover', this.onPointerOver, { passive: true });
      window.addEventListener('resize', this.onResize);

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.addEventListener('on:cart-drawer:after-open', this.prefetchVisibleEdits);
        cartDrawer.addEventListener('on:cart:after-merge', this.prefetchVisibleEdits);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keyup', this.onKeyUp);
      document.removeEventListener('pointerover', this.onPointerOver);
      window.removeEventListener('resize', this.onResize);
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.removeEventListener('on:cart-drawer:after-open', this.prefetchVisibleEdits);
        cartDrawer.removeEventListener('on:cart:after-merge', this.prefetchVisibleEdits);
      }
      this.teardownSwipe();
    }

    handlePointerOver(evt) {
      const editBtn = evt.target.closest?.('[data-cart-edit]');
      if (!editBtn) return;
      this.prefetchEdit(editBtn.dataset.productUrl, editBtn.dataset.variantId);
    }

    prefetchVisibleEdits() {
      document.querySelectorAll('[data-cart-edit]').forEach((btn) => {
        this.prefetchEdit(btn.dataset.productUrl, btn.dataset.variantId);
      });
    }

    prefetchEdit(productUrl, variantId) {
      if (!productUrl) return;
      const key = cacheKeyFor(productUrl, variantId);
      if (sectionCache.has(key) || sectionInflight.has(key)) return;
      fetchEditSection(productUrl, variantId).catch(() => {});
    }

    handleDocumentClick(evt) {
      const editBtn = evt.target.closest('[data-cart-edit]');
      if (editBtn) {
        evt.preventDefault();
        this.openFromButton(editBtn);
        return;
      }

      if (!this.hasAttribute('open')) return;

      if (evt.target.closest('.js-cart-edit-close') || evt.target === this.overlay) {
        evt.preventDefault();
        this.close();
      }
    }

    handleKeyUp(evt) {
      if (evt.key === 'Escape' && this.hasAttribute('open')) {
        this.close();
      }
    }

    handleResize() {
      if (!this.hasAttribute('open') || !this.galleryRoot) return;
      this.updateThumbsNav();
      this.goToMedia(this.activeMediaIndex, false);
    }

    openFromButton(btn) {
      let properties = {};
      try {
        properties = JSON.parse(btn.dataset.properties || '{}');
        if (Array.isArray(properties)) {
          properties = Object.fromEntries(properties);
        }
      } catch (e) {
        properties = {};
      }

      this.lineContext = {
        productUrl: btn.dataset.productUrl,
        variantId: btn.dataset.variantId,
        lineKey: btn.dataset.lineKey,
        line: Number(btn.dataset.line),
        quantity: Number(btn.dataset.quantity) || 1,
        properties,
        sellingPlan: btn.dataset.sellingPlan || null
      };

      this.preferredOptions = null;
      this.open();
      this.loadProduct(this.lineContext.productUrl, this.lineContext.variantId);
    }

    open() {
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cart-edit-open');
      document.addEventListener('keyup', this.onKeyUp);

      requestAnimationFrame(() => {
        this.classList.add('is-visible');
      });
    }

    close() {
      this.classList.remove('is-visible');
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cart-edit-open');
      document.removeEventListener('keyup', this.onKeyUp);
      this.teardownSwipe();
      this.galleryRoot = null;

      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      const duration = parseFloat(getComputedStyle(this).getPropertyValue('--cart-edit-duration')) || 340;
      setTimeout(() => {
        if (!this.hasAttribute('open') && this.content) {
          this.content.innerHTML = '';
        }
      }, duration);
    }

    async loadProduct(productUrl, variantId, preferredOptions = null) {
      if (!productUrl || !this.content) return;

      this.loadRequestId = (this.loadRequestId || 0) + 1;
      const requestId = this.loadRequestId;

      const cacheKey = cacheKeyFor(productUrl, variantId);
      const hasCache = sectionCache.has(cacheKey);
      if (!hasCache) {
        this.content.innerHTML = '<div class="cart-edit__loading"><div class="loading-spinner"></div></div>';
      }
      this.preferredOptions = preferredOptions;
      this.galleryRoot = null;

      try {
        const html = await fetchEditSection(productUrl, variantId);
        if (requestId !== this.loadRequestId || !this.hasAttribute('open')) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const editEl = doc.querySelector('.cart-edit');
        if (!editEl) throw new Error('Edit content missing');

        this.content.innerHTML = '';
        this.content.appendChild(editEl);
        this.bindContent(editEl);

        if (preferredOptions) {
          this.applyPreferredOptions(preferredOptions);
        }

        const closeBtn = editEl.querySelector('.js-cart-edit-close');
        if (closeBtn) closeBtn.focus();
      } catch (error) {
        if (requestId !== this.loadRequestId) return;
        console.warn('[cart-edit]', error);
        this.content.innerHTML = `<div class="cart-edit__error">${theme.strings.cartItemsUpdateError || 'Unable to load editor.'}</div>`;
      }
    }

    bindContent(root) {
      const jsonEl = root.querySelector('[data-cart-edit-json]');
      try {
        this.productData = jsonEl ? JSON.parse(jsonEl.textContent) : null;
      } catch (e) {
        this.productData = null;
      }

      this.galleryRoot = root;
      this.thumbOffset = 0;
      this.setupGalleryControls(root);

      root.querySelectorAll('.cart-edit__option-input').forEach((input) => {
        input.addEventListener('change', () => this.handleOptionChange(input, root));
      });

      const updateBtn = root.querySelector('[data-cart-edit-update]');
      if (updateBtn) {
        updateBtn.addEventListener('click', () => this.submitUpdate(updateBtn));
      }

      this.syncFromSelection(root);
    }

    setupGalleryControls(root) {
      const prevBtn = root.querySelector('[data-cart-edit-thumbs-prev]');
      const nextBtn = root.querySelector('[data-cart-edit-thumbs-next]');
      const thumbs = root.querySelector('[data-cart-edit-thumbs]');
      if (prevBtn) {
        prevBtn.addEventListener('click', () => this.shiftThumbs(-1));
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => this.shiftThumbs(1));
      }
      if (thumbs) {
        thumbs.addEventListener('scroll', () => this.updateThumbsNav(), { passive: true });
      }
      this.setupSwipe(root);
    }

    handleOptionChange(input, root) {
      const productUrl = input.dataset.productUrl;
      const isLinked = input.dataset.linkedProduct === 'true';
      const currentUrl = root.dataset.productUrl;

      if (isLinked && productUrl && productUrl !== currentUrl) {
        const preferred = this.getSelectedOptions(root);
        const optionIndex = Number(input.closest('[data-option-index]')?.dataset.optionIndex);
        this.loadProduct(productUrl, null, preferred.map((value, index) => (
          index === optionIndex ? null : value
        )));
        return;
      }

      this.syncFromSelection(root);
    }

    getSelectedOptions(root = this.content) {
      const fieldsets = [...root.querySelectorAll('.cart-edit__option')];
      return fieldsets.map((fieldset) => {
        const checked = fieldset.querySelector('.cart-edit__option-input:checked');
        return checked ? checked.value : null;
      });
    }

    applyPreferredOptions(preferredOptions) {
      const root = this.content.querySelector('.cart-edit');
      if (!root || !preferredOptions) return;

      preferredOptions.forEach((value, index) => {
        if (!value) return;
        const fieldset = root.querySelector(`.cart-edit__option[data-option-index="${index}"]`);
        if (!fieldset) return;
        const input = [...fieldset.querySelectorAll('.cart-edit__option-input')]
          .find((el) => el.value === value && !el.dataset.linkedProduct);
        if (input) {
          input.checked = true;
        }
      });

      this.syncFromSelection(root);
    }

    findVariant(selectedOptions) {
      if (!this.productData?.variants) return null;
      return this.productData.variants.find((variant) => (
        variant.options.every((option, index) => option === selectedOptions[index])
      )) || null;
    }

    syncFromSelection(root) {
      const selectedOptions = this.getSelectedOptions(root);
      this.selectedVariant = this.findVariant(selectedOptions);

      root.querySelectorAll('.cart-edit__option').forEach((fieldset, index) => {
        const valueEl = fieldset.querySelector('[data-option-value]');
        if (valueEl) valueEl.textContent = selectedOptions[index] || '';
      });

      this.updateAvailability(root, selectedOptions);
      this.updatePrice(root);
      this.refreshGallery(root);
      this.updateButton(root);
    }

    updateAvailability(root, selectedOptions) {
      if (!this.productData?.variants) return;

      root.querySelectorAll('.cart-edit__option').forEach((fieldset, selectorIndex) => {
        fieldset.querySelectorAll('.cart-edit__option-input:not([data-linked-product])').forEach((input) => {
          let exists = false;
          let available = false;

          this.productData.variants.forEach((variant) => {
            let matches = true;
            variant.options.forEach((option, optionIndex) => {
              if (optionIndex === selectorIndex) return;
              if (selectedOptions[optionIndex] && option !== selectedOptions[optionIndex]) {
                matches = false;
              }
            });
            if (matches && variant.options[selectorIndex] === input.value) {
              exists = true;
              if (variant.available) available = true;
            }
          });

          input.classList.toggle('is-unavailable', !available);
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !available);
          input.disabled = !exists;
        });
      });
    }

    updatePrice(root) {
      const priceEl = root.querySelector('[data-cart-edit-price]');
      if (!priceEl || !this.selectedVariant || !this.productData?.formatted) return;
      const formatted = this.productData.formatted[this.selectedVariant.id];
      if (formatted?.price) priceEl.textContent = formatted.price;
    }

    /**
     * Media for current color: from this color's first featured image
     * until the next other-color featured image.
     */
    getMediaForCurrentColor() {
      const media = this.productData?.media || [];
      if (!media.length) return [];

      const groupIndex = this.productData.groupOptionIndex;
      if (groupIndex < 0 || !this.selectedVariant) {
        return media.slice();
      }

      const colorValue = this.selectedVariant.options[groupIndex];
      const thisColorFeatured = new Set();
      const otherColorFeatured = new Set();

      this.productData.variants.forEach((variant) => {
        if (!variant.featured_media_id) return;
        if (variant.options[groupIndex] === colorValue) {
          thisColorFeatured.add(String(variant.featured_media_id));
        } else {
          otherColorFeatured.add(String(variant.featured_media_id));
        }
      });

      const allGroups = media.filter((item) => item.all_groups);
      const regular = media.filter((item) => !item.all_groups);

      if (!thisColorFeatured.size) {
        const featuredId = this.selectedVariant.featured_media_id;
        if (featuredId) {
          const featured = regular.find((item) => String(item.id) === String(featuredId));
          return featured ? [featured, ...allGroups] : [...regular, ...allGroups];
        }
        return [...regular, ...allGroups];
      }

      let start = regular.findIndex((item) => thisColorFeatured.has(String(item.id)));
      if (start < 0) start = 0;

      const grouped = [];
      for (let i = start; i < regular.length; i += 1) {
        const item = regular[i];
        const id = String(item.id);
        if (i > start && otherColorFeatured.has(id) && !thisColorFeatured.has(id)) {
          break;
        }
        grouped.push(item);
      }

      return [...grouped, ...allGroups.filter((item) => !grouped.some((g) => g.id === item.id))];
    }

    refreshGallery(root) {
      this.currentMedia = this.getMediaForCurrentColor();

      let startIndex = 0;
      if (this.selectedVariant?.featured_media_id) {
        const idx = this.currentMedia.findIndex(
          (item) => String(item.id) === String(this.selectedVariant.featured_media_id)
        );
        if (idx >= 0) startIndex = idx;
      }

      this.renderThumbs(root);
      this.renderMainTrack(root);
      this.goToMedia(startIndex, false);
      requestAnimationFrame(() => {
        const thumbs = root.querySelector('[data-cart-edit-thumbs]');
        if (thumbs) thumbs.scrollTop = 0;
        this.updateThumbsNav();
        this.ensureThumbVisible(this.activeMediaIndex);
      });
    }

    renderThumbs(root) {
      const thumbs = root.querySelector('[data-cart-edit-thumbs]');
      if (!thumbs) return;

      thumbs.innerHTML = '';
      this.currentMedia.forEach((item, index) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cart-edit__thumb';
        btn.dataset.mediaId = item.id;
        btn.dataset.index = String(index);
        btn.style.backgroundImage = `url(${item.thumb || item.src})`;
        btn.setAttribute('aria-label', `Image ${index + 1}`);
        btn.addEventListener('click', () => this.goToMedia(index, true));
        thumbs.appendChild(btn);
      });
    }

    renderMainTrack(root) {
      const track = root.querySelector('[data-cart-edit-track]');
      const dots = root.querySelector('[data-cart-edit-dots]');
      if (!track) return;

      track.innerHTML = '';
      if (dots) dots.innerHTML = '';

      this.currentMedia.forEach((item, index) => {
        const slide = document.createElement('div');
        slide.className = 'cart-edit__main-slide';
        slide.dataset.index = String(index);
        const img = document.createElement('img');
        img.className = 'cart-edit__main-image';
        img.src = item.src;
        img.alt = item.alt || '';
        img.loading = index === 0 ? 'eager' : 'lazy';
        img.draggable = false;
        slide.appendChild(img);
        track.appendChild(slide);

        if (dots && this.currentMedia.length > 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'cart-edit__main-dot';
          dot.setAttribute('aria-label', `Go to image ${index + 1}`);
          dot.addEventListener('click', () => this.goToMedia(index, true));
          dots.appendChild(dot);
        }
      });

      if (dots) {
        dots.hidden = this.currentMedia.length < 2;
      }
    }

    goToMedia(index, syncThumbWindow = true) {
      if (!this.currentMedia.length || !this.galleryRoot) return;

      const max = this.currentMedia.length - 1;
      this.activeMediaIndex = Math.max(0, Math.min(index, max));

      const track = this.galleryRoot.querySelector('[data-cart-edit-track]');
      if (track) {
        track.classList.remove('is-dragging');
        track.style.transform = `translate3d(-${this.activeMediaIndex * 100}%, 0, 0)`;
      }

      this.galleryRoot.querySelectorAll('.cart-edit__thumb').forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === this.activeMediaIndex);
      });

      this.galleryRoot.querySelectorAll('.cart-edit__main-dot').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.activeMediaIndex);
      });

      if (syncThumbWindow) {
        this.ensureThumbVisible(this.activeMediaIndex);
      }
      this.updateThumbsNav();
    }

    updateThumbsNav() {
      if (!this.galleryRoot) return;
      const thumbs = this.galleryRoot.querySelector('[data-cart-edit-thumbs]');
      const prevBtn = this.galleryRoot.querySelector('[data-cart-edit-thumbs-prev]');
      const nextBtn = this.galleryRoot.querySelector('[data-cart-edit-thumbs-next]');
      if (!thumbs || !prevBtn || !nextBtn) return;

      const canScroll = thumbs.scrollHeight > thumbs.clientHeight + 2;
      prevBtn.hidden = !canScroll;
      nextBtn.hidden = !canScroll;
      if (!canScroll) {
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        return;
      }

      prevBtn.disabled = thumbs.scrollTop <= 2;
      nextBtn.disabled = thumbs.scrollTop + thumbs.clientHeight >= thumbs.scrollHeight - 2;
    }

    shiftThumbs(direction) {
      if (!this.galleryRoot) return;
      const thumbs = this.galleryRoot.querySelector('[data-cart-edit-thumbs]');
      if (!thumbs) return;
      const step = (THUMB_HEIGHT + THUMB_GAP) * 2;
      thumbs.scrollBy({ top: direction * step, behavior: 'smooth' });
    }

    ensureThumbVisible(index) {
      if (!this.galleryRoot) return;
      const thumbs = this.galleryRoot.querySelector('[data-cart-edit-thumbs]');
      const thumb = this.galleryRoot.querySelectorAll('.cart-edit__thumb')[index];
      if (!thumbs || !thumb) return;
      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      this.updateThumbsNav();
    }

    setupSwipe(root) {
      this.teardownSwipe();
      const viewport = root.querySelector('.cart-edit__main-viewport');
      const track = root.querySelector('[data-cart-edit-track]');
      if (!viewport || !track) return;

      let startX = 0;
      let startY = 0;
      let deltaX = 0;
      let tracking = false;
      let horizontal = null;

      const isMobile = () => !window.matchMedia('(min-width: 768px)').matches;

      const onStart = (evt) => {
        if (!isMobile()) return;
        if (this.currentMedia.length < 2) return;
        const point = evt.touches ? evt.touches[0] : evt;
        startX = point.clientX;
        startY = point.clientY;
        deltaX = 0;
        tracking = true;
        horizontal = null;
        track.classList.add('is-dragging');
      };

      const onMove = (evt) => {
        if (!tracking) return;
        const point = evt.touches ? evt.touches[0] : evt;
        deltaX = point.clientX - startX;
        const deltaY = point.clientY - startY;

        if (horizontal === null && (Math.abs(deltaX) > 6 || Math.abs(deltaY) > 6)) {
          horizontal = Math.abs(deltaX) > Math.abs(deltaY);
          if (!horizontal) {
            tracking = false;
            track.classList.remove('is-dragging');
            return;
          }
        }

        if (!horizontal) return;
        if (evt.cancelable) evt.preventDefault();

        const width = viewport.clientWidth || 1;
        let offsetPx = (-this.activeMediaIndex * width) + deltaX;

        // Soft resistance at ends
        if (this.activeMediaIndex === 0 && deltaX > 0) {
          offsetPx = deltaX * 0.35;
        } else if (this.activeMediaIndex >= this.currentMedia.length - 1 && deltaX < 0) {
          offsetPx = (-this.activeMediaIndex * width) + (deltaX * 0.35);
        }

        track.style.transform = `translate3d(${offsetPx}px, 0, 0)`;
      };

      const onEnd = () => {
        if (!tracking && horizontal === null) return;
        const wasHorizontal = horizontal;
        tracking = false;
        horizontal = null;
        track.classList.remove('is-dragging');

        if (!wasHorizontal) {
          this.goToMedia(this.activeMediaIndex, false);
          return;
        }

        const width = viewport.clientWidth || 1;
        const threshold = Math.min(60, width * 0.18);
        if (deltaX < -threshold) this.goToMedia(this.activeMediaIndex + 1, true);
        else if (deltaX > threshold) this.goToMedia(this.activeMediaIndex - 1, true);
        else this.goToMedia(this.activeMediaIndex, false);
      };

      viewport.addEventListener('touchstart', onStart, { passive: true });
      viewport.addEventListener('touchmove', onMove, { passive: false });
      viewport.addEventListener('touchend', onEnd);
      viewport.addEventListener('touchcancel', onEnd);

      this._swipeCleanup = () => {
        viewport.removeEventListener('touchstart', onStart);
        viewport.removeEventListener('touchmove', onMove);
        viewport.removeEventListener('touchend', onEnd);
        viewport.removeEventListener('touchcancel', onEnd);
      };
    }

    teardownSwipe() {
      if (typeof this._swipeCleanup === 'function') {
        this._swipeCleanup();
        this._swipeCleanup = null;
      }
    }

    updateButton(root) {
      const btn = root.querySelector('[data-cart-edit-update]');
      if (!btn) return;

      if (!btn.dataset.label) {
        btn.dataset.label = btn.textContent.trim();
      }

      const available = this.selectedVariant && this.selectedVariant.available;
      btn.disabled = !available;

      if (!this.selectedVariant) {
        btn.textContent = theme.strings.noVariant || 'Unavailable';
      } else if (!this.selectedVariant.available) {
        btn.textContent = theme.strings.noStock || 'Sold out';
      } else {
        btn.textContent = btn.dataset.label;
      }
    }

    hasLineExtras() {
      if (!this.lineContext) return false;
      if (this.lineContext.sellingPlan) return true;
      const properties = this.lineContext.properties;
      if (!properties || typeof properties !== 'object') return false;
      return Object.keys(properties).some((key) => properties[key] != null && properties[key] !== '');
    }

    async submitUpdate(btn) {
      if (!this.selectedVariant || !this.lineContext || btn.disabled) return;

      const newVariantId = Number(this.selectedVariant.id);
      const sameVariant = String(newVariantId) === String(this.lineContext.variantId);
      if (sameVariant) {
        this.close();
        return;
      }

      if (!Number.isFinite(newVariantId) || newVariantId <= 0) {
        if (typeof theme.showQuickPopup === 'function') {
          theme.showQuickPopup(theme.strings.cartItemsUpdateError || 'Unable to update this item.', btn);
        }
        return;
      }

      const lineKey = this.lineContext.lineKey;
      const quantity = Number(this.lineContext.quantity) || 1;
      const properties = this.lineContext.properties;
      const sellingPlan = this.lineContext.sellingPlan;
      const hasExtras = this.hasLineExtras();
      const selectedVariant = this.selectedVariant;
      const productData = this.productData;

      if (!lineKey) {
        if (typeof theme.showQuickPopup === 'function') {
          theme.showQuickPopup(theme.strings.cartItemsUpdateError || 'Unable to update this item.', btn);
        }
        return;
      }

      // Optimistic: close immediately, patch cart line, sync in background
      this.applyOptimisticLinePatch(lineKey, selectedVariant, productData);
      this.close();

      try {
        if (hasExtras) {
          await this.replaceLineWithExtras(lineKey, newVariantId, quantity, properties, sellingPlan);
        } else {
          const response = await fetch(theme.routes.cartUpdate, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              updates: {
                [lineKey]: 0,
                [newVariantId]: quantity
              }
            })
          });

          if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.description || err.message || `HTTP ${response.status}`);
          }
        }

        document.dispatchEvent(new CustomEvent('on:cart:change', {
          bubbles: true,
          detail: { silent: true }
        }));
      } catch (error) {
        console.warn('[cart-edit]', error);
        if (typeof theme.showQuickPopup === 'function') {
          const cartForm = document.querySelector('cart-form.cart-drawer') || document.querySelector('cart-form');
          theme.showQuickPopup(
            theme.strings.cartItemsUpdateError || error.message || 'Unable to update this item.',
            cartForm || document.body
          );
        }
        document.dispatchEvent(new CustomEvent('on:cart:change', {
          bubbles: true,
          detail: { silent: true }
        }));
      }
    }

    applyOptimisticLinePatch(lineKey, variant, productData) {
      const input = [...document.querySelectorAll('.cart-item__quantity-input')]
        .find((el) => el.dataset.key === lineKey);
      const cartItem = input?.closest('.cart-item');
      if (!cartItem || !variant) return;

      const listItem = cartItem.closest('[data-merge-list-item]');
      listItem?.classList.add('merge-item-refreshing');

      if (variant.featured_image) {
        const img = cartItem.querySelector('.cart-item__image img');
        if (img) {
          img.src = variant.featured_image;
          img.removeAttribute('srcset');
        }
      }

      const optionValues = cartItem.querySelectorAll('.cart-item__variant-value');
      optionValues.forEach((el, index) => {
        if (variant.options?.[index] != null) {
          el.textContent = variant.options[index];
        }
      });

      const priceEl = cartItem.querySelector('.cart-item__selling-price');
      const formatted = productData?.formatted?.[variant.id];
      if (priceEl && formatted?.price) {
        priceEl.textContent = formatted.price;
      }
    }

    async replaceLineWithExtras(lineKey, newVariantId, quantity, properties, sellingPlan) {
      const removeResponse = await fetch(theme.routes.cartChange, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0
        })
      });

      if (!removeResponse.ok) {
        const err = await removeResponse.json().catch(() => ({}));
        throw new Error(err.description || err.message || `HTTP ${removeResponse.status}`);
      }

      const addItem = {
        id: newVariantId,
        quantity
      };

      if (properties && Object.keys(properties).length) {
        addItem.properties = properties;
      }
      if (sellingPlan) {
        addItem.selling_plan = Number(sellingPlan);
      }

      const addResponse = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: [addItem] })
      });

      if (!addResponse.ok) {
        const err = await addResponse.json().catch(() => ({}));
        throw new Error(err.description || err.message || `HTTP ${addResponse.status}`);
      }
    }
  }

  if (!customElements.get('cart-edit-modal')) {
    customElements.define('cart-edit-modal', CartEditModal);
  }

  if (theme.strings) {
    theme.strings.cartItemsUpdateItem = theme.strings.cartItemsUpdateItem || 'Update Item';
    theme.strings.cartItemsUpdateError = theme.strings.cartItemsUpdateError || 'Unable to update this item. Please try again.';
  }
})();
